import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiDynamicIcon,
  enumContainer,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  omitNullsy,
  RISKINESS_COLORS,
  useLocationQuery,
  useXlOrLarger,
  MaximizeButton,
  useMaximizeWrapper,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  clampPageNumber,
  DateFormatter,
  DEFAULT_PAGE_SIZE,
  getDefaultMTOptions,
  HospitalizationsLinkFormatter,
  LightDateRangeFilter,
  LightFilterPanel,
  LightSelectedFilters,
  LightSelectFilter,
  LightTextFilter,
  ListDepartmentFormatter,
  MaterialTableWrapper,
  MerieSchema,
  PhRecommendationRiskFactorsFormatter,
  TablePaging,
  TableSorting,
  useFormatters,
  usePermissions,
  UserFilterDto,
  useTableData,
  useTableParams,
  flattenTableParams,
  useUserFilters,
  BOOL_FILTER_OPTIONS,
  Formatters,
  TooltipWrapper,
  LightOrganizationFilter2,
  ChipsFormatter,
  PhRecommendationDrpMedicationFormatter,
  PhRecommendationDrpTypesFormatter,
  PhRecommendationDrpCausesFormatter,
  PhRecommendationDrpInterventionsFormatter,
  getDrpsHeight,
  FreeSoloValue,
  LightSelectFilterVirtualized,
  LightHierarchyFilter,
  NormalizedOption,
  isFreeSolo,
  PhRecommendationFollowUpDateFormatter,
  getSingleBoolFilterValue,
  getSingleBoolFilterOptionId,
  MedicationAtc,
  useDRPMedicationData,
  useInstanceLocation,
  ShortcutEnum,
  useInactiveAtcRenderProps,
  LightSelectedFiltersPane,
  useOrganizations,
  LightFilterTabDef,
  SelectedFilter,
  countSelectedFilters,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo, useRef} from 'react';
import {Helmet} from 'react-helmet';

import {getIsPhRecUnpublished} from './hospitalizations_detail/HospitalizationPhRecommendationPanelV2';
import {isKnownMedicationName, useMedicationOptions} from './hospitalizations_detail/HospitalizationPhRecommendationV2';
import {usePhRecommendationsExport} from './usePhRecommendationsExport';
import {useAppSelector} from 'src/js/store_hooks';

const PATIENTS_LAYOUT_SPLIT_CONFIG = [
  {
    sizes: [233, 767],
    minSize: [64, 100],
    layoutId: '/LightSplitLayout.PharmacotherapyRecommendationListLayout',
  },
];
type Props = {
  config: ConfigObjectType;
};

type PharmaRecommendationFilterDto = MerieSchema.components['schemas']['PharmaRecommendationFilterDto'];
type DrugRelatedProblemFilterDto = MerieSchema.components['schemas']['DrugRelatedProblemFilterDto'];
export type PhRecPageFilters = Omit<PharmaRecommendationFilterDto, 'drpFilter'> & {
  drpFilter?: Omit<DrugRelatedProblemFilterDto, 'medicationIds' | 'medicationUserInput' | 'atcHierarchyIds'> & {
    medicationName_local?: string | FreeSoloValue;
    medicationAtcId?: number[];
    statusAssessedOnFloatingShortcutName?: ShortcutEnum | null;
  };
  pharmaRecommendationFloatingShortcutName?: ShortcutEnum | null;
  encounterFloatingShortcutName?: ShortcutEnum | null;
  followUpFloatingShortcutName?: ShortcutEnum | null;
};
/** Filter-panel tabs (PDM-2750): the recommendation fields and the DRP fields. */
export type PhRecFilterTab = 'general' | 'drp';
export type FiltersData = {
  recommendationOpen: boolean;
  /** Selected filter-panel tab; part of the page state so it survives a reload like the open flag does. */
  recommendationTab: PhRecFilterTab;
  recommendation: PhRecPageFilters;
  recommendationSorting: TableSorting;
  recommendationPaging: TablePaging;
};
const DEFAULT_RECOMMENDATION_OPEN = true;
const DEFAULT_RECOMMENDATION_TAB: PhRecFilterTab = 'general';
const DEFAULT_RECOMMENDATION_FILTERS: PhRecPageFilters = {};
const DEFAULT_RECOMMENDATION_SORTING: TableSorting = {};
const DEFAULT_RECOMMENDATION_PAGING: TablePaging = {};

export const PhRecommendationListLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'pharmacotherapyRecommendationList';
  const storageIdPrefix = '/pharmacotherapyRecommendationList';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      recommendationOpen: query.recommendationOpen ?? DEFAULT_RECOMMENDATION_OPEN,
      recommendationTab: query.recommendationTab ?? DEFAULT_RECOMMENDATION_TAB,
      recommendation: query?.recommendation ?? DEFAULT_RECOMMENDATION_FILTERS,
      recommendationSorting: query?.recommendationSorting ?? DEFAULT_RECOMMENDATION_SORTING,
      recommendationPaging: query?.recommendationPaging ?? DEFAULT_RECOMMENDATION_PAGING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({recommendation: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <PhRecommendationListLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
type RowType = MerieSchema.components['schemas']['PharmaRecommendationPageDto'];

function useColumns(formatters: Formatters, defaultSort: TableSorting, config: ConfigObjectType): Column<RowType>[] {
  const {t, tEnum, formatDate} = formatters;
  const xlOrLarger = useXlOrLarger();
  const columns: Column<RowType>[] = [
    {
      // ['patientFullName', 'patientIdentificationNumber (no sort)', 'encounterOrganization', 'encounterStartedAt', 'encounterEndedAt (no sort)']
      field: 'visit',
      title: t('phList.visit'),
      width: xlOrLarger ? '' : '220px',
      render: (data) => {
        const getTexts = (long: boolean) => [
          `${data.patient?.fullName} (${data.patient.identificationNumber})`,
          long ? data.encounterOrganization.longName : data.encounterOrganization.shortName,
          `${formatDate(data.encounterStartedAt)} - ${
            data.encounterEndedAt ? formatDate(data.encounterEndedAt) : t('case.encounter.inProgress')
          }`,
        ];
        const label = getTexts(false).join(', ');

        const accTooltip = [] as React.ReactNode[];
        getTexts(true).forEach((text, i) => {
          if (i > 0) accTooltip.push(<br key={`${i}-br`} />);
          accTooltip.push(<span key={i}>{text}</span>);
        });
        // NOTE: tooltip has `maxWidth: 300`
        accTooltip.push(<hr key='divider' className='tooltip-divider' />);
        accTooltip.push(
          <div key='conclusion' data-ellipsis='3'>
            {data.conclusion}
          </div>
        );
        const tooltip = <>{accTooltip}</>;

        return (
          <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
            <HospitalizationsLinkFormatter
              wrapperStyle={{maxWidth: 'calc(100% - 24px)'}}
              {...AG_GRID_COMPATIBILITY}
              value={label}
              tooltip={tooltip}
              data={{id: data.evaluatedEncounterId}}
              versionSuffix='/v2'
              style={{padding: 0}}
              phRecId={data.id}
            />
            {getIsPhRecUnpublished(data) && (
              <TooltipWrapper wrapperStyle={{marginTop: 0, padding: 0}} tooltip={t('hospitalization.detail.phRec.unpublished')}>
                <DlMuiDynamicIcon
                  iconName='merieSpikedExclamationIcon'
                  useFixedViewBox={14}
                  style={{padding: 0, marginLeft: 4}}
                  svgIconStyle={{
                    width: 18,
                    height: 18,
                  }}
                />
              </TooltipWrapper>
            )}
          </div>
        );
      },
    },
    {
      field: 'RISKINESS',
      title: t('hospitalization.detail.phRec.riskiness'),
      render: (data) => {
        const label = tEnum('Riskiness', data.riskiness ?? '');
        return (
          <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
            <ChipsFormatter
              value={[data.riskiness]}
              getInfo={(riskiness) => ({
                label,
                background: RISKINESS_COLORS[riskiness]?.background,
              })}
              tooltip={label}
            />
          </div>
        );
      },
      width: xlOrLarger ? '157px' : '',
    },
    {
      field: 'DEPARTMENT',
      title: t('phList.recommendationOrganization'),
      render: (data) => (
        <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
          <ListDepartmentFormatter value={data.recommendationOrganization} />
        </div>
      ),
      width: xlOrLarger ? '125px' : '',
      cellStyle: {
        lineHeight: '0',
      },
    },
    {
      field: 'DATE',
      title: t('phList.recommendationDate'),
      render: (data) => (
        <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
          <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.recommendationDate} />
        </div>
      ),
      width: xlOrLarger ? '148px' : '',
    },
    {
      field: 'RISK_FACTORS_COUNT',
      title: t('hospitalization.detail.phRec.riskFactors'),
      render: (data) => (
        <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
          <PhRecommendationRiskFactorsFormatter data={data} />
        </div>
      ),
      width: xlOrLarger ? '116px' : '',
    },
    {
      field: 'FOLLOW_UP_DATE',
      title: t('patient.list.grid.pharmaRecommendationFollowUpDate'),
      render: (data) => (
        <div style={{minHeight: getDrpsHeight(data.drugRelatedProblems), display: 'flex', alignItems: 'flex-start'}}>
          <PhRecommendationFollowUpDateFormatter value={data.followUpDate} />
        </div>
      ),
      width: xlOrLarger ? '143px' : '',
      cellStyle: (data, rowData) => ({
        paddingTop: rowData.drugRelatedProblems.length > 1 && typeof rowData.followUpDate === 'string' ? '0px' : undefined,
        lineHeight: rowData.drugRelatedProblems.length > 1 && typeof rowData.followUpDate === 'undefined' ? undefined : 2.2,
      }),
    },
    {
      field: 'medication',
      title: t('phList.medication'),
      render: (data) => (
        <PhRecommendationDrpMedicationFormatter dividerWidthOffset={22} problems={data.drugRelatedProblems} config={config} />
      ),
      sorting: false,
    },
    {
      field: 'type',
      title: t('hospitalization.detail.phRec.drp.type'),
      render: (data) => <PhRecommendationDrpTypesFormatter dividerWidthOffset={22} problems={data.drugRelatedProblems} />,
      sorting: false,
    },
    {
      field: 'cause',
      title: t('hospitalization.detail.phRec.drp.causes'),
      render: (data) => <PhRecommendationDrpCausesFormatter dividerWidthOffset={22} problems={data.drugRelatedProblems} />,
      sorting: false,
    },
    {
      field: 'intervention',
      title: t('hospitalization.detail.phRec.drp.plannedInterventions'),
      render: (data) => <PhRecommendationDrpInterventionsFormatter dividerWidthOffset={0} problems={data.drugRelatedProblems} />,
      sorting: false,
    },
  ];
  return columns.map((v) => ({
    ...v,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

export const getMedicationFilterData = (drpFilter: any) => {
  const accFilterData: Pick<DrugRelatedProblemFilterDto, 'medicationIds' | 'medicationUserInput' | 'atcHierarchyIds'> = {};
  const {medicationName_local, medicationAtcId} = drpFilter ?? {};
  if (medicationName_local != null) {
    if (isFreeSolo(medicationName_local)) {
      accFilterData.medicationUserInput = medicationName_local.freeSolo;
    } else {
      accFilterData.medicationIds = [+medicationName_local];
    }
  }
  if (medicationAtcId != null) {
    accFilterData.atcHierarchyIds = medicationAtcId.map((value) => +value);
  }
  return accFilterData;
};

function normalizePhRecFiltersForCompare(filter: PhRecPageFilters): PhRecPageFilters {
  const copy: PhRecPageFilters = structuredClone(filter ?? ({} as PhRecPageFilters));

  if (copy.encounterFloatingShortcutName) {
    delete (copy as any).encounterStartedFrom;
    delete (copy as any).encounterStartedTo;
  }

  if (copy.pharmaRecommendationFloatingShortcutName) {
    delete (copy as any).pharmaRecommendationDateFrom;
    delete (copy as any).pharmaRecommendationDateTo;
  }

  if (copy.followUpFloatingShortcutName) {
    delete (copy as any).followUpDateFrom;
    delete (copy as any).followUpDateTo;
  }

  if (copy.drpFilter?.statusAssessedOnFloatingShortcutName) {
    delete (copy.drpFilter as any).statusAssessedOnFrom;
    delete (copy.drpFilter as any).statusAssessedOnTo;
  }

  return copy;
}
const PhRecommendationListLayoutInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const {isLoading: permissionsLoading} = usePermissions(config);

  const hasFilters = !isEqual(omitNullsy(filters.recommendation), omitNullsy(DEFAULT_RECOMMENDATION_FILTERS));

  const {getDRPMedicationData, medications} = useDRPMedicationData(config);
  const {medicationNameOptions, medicationAtcOptions /* , medicationAtcOptionsLoading */} = useMedicationOptions({
    medications,
    getDRPMedicationData,
    config,
    selectedMedicationName: filters.recommendation.drpFilter?.medicationName_local,
  });

  // all rec list
  const recommendationsUrl = config.service.url.recommendations;
  const recommendationsParams = useTableParams<PharmaRecommendationFilterDto>(
    flattenTableParams({
      patientFullName: filters.recommendation.patientFullName,
      patientIdentificationNumber: filters.recommendation.patientIdentificationNumber,
      patientSrcId: filters.recommendation.patientSrcId,
      encounterStartedFrom: filters.recommendation.encounterStartedFrom,
      encounterStartedTo: filters.recommendation.encounterStartedTo,
      encounterFloatingShortcutName: filters.recommendation.encounterFloatingShortcutName,
      pharmaRecommendationOrganizationIds:
        filters.recommendation.pharmaRecommendationOrganizationIds && filters.recommendation.pharmaRecommendationOrganizationIds.join(','),
      pharmaRecommendationDateFrom: filters.recommendation.pharmaRecommendationDateFrom,
      pharmaRecommendationDateTo: filters.recommendation.pharmaRecommendationDateTo,
      pharmaRecommendationFloatingShortcutName: filters.recommendation.pharmaRecommendationFloatingShortcutName,
      wasPublished: filters.recommendation.wasPublished,
      riskiness: filters.recommendation.riskiness,
      riskFactorTypes: filters.recommendation.riskFactorTypes && filters.recommendation.riskFactorTypes.join(','),
      summaryAndConclusionSearchString: filters.recommendation.summaryAndConclusionSearchString,
      hasDrp: filters.recommendation.hasDrp,
      followUpDateFrom: filters.recommendation.followUpDateFrom,
      followUpDateTo: filters.recommendation.followUpDateTo,
      followUpFloatingShortcutName: filters.recommendation.followUpFloatingShortcutName,
      procedures: filters.recommendation.procedures && filters.recommendation.procedures.join(','),
      drpFilter: {
        ...getMedicationFilterData(filters.recommendation.drpFilter),
        types: filters.recommendation.drpFilter?.types && filters.recommendation.drpFilter.types.join(','),
        causes: filters.recommendation.drpFilter?.causes && filters.recommendation.drpFilter.causes.join(','),
        plannedInterventions:
          filters.recommendation.drpFilter?.plannedInterventions && filters.recommendation.drpFilter.plannedInterventions.join(','),
        manifestation: filters.recommendation.drpFilter?.manifestation,
        interventionAcceptances:
          filters.recommendation.drpFilter?.interventionAcceptances && filters.recommendation.drpFilter.interventionAcceptances.join(','),
        interventionNotAcceptedReasons:
          filters.recommendation.drpFilter?.interventionNotAcceptedReasons &&
          filters.recommendation.drpFilter.interventionNotAcceptedReasons.join(','),
        statuses: filters.recommendation.drpFilter?.statuses && filters.recommendation.drpFilter.statuses.join(','),
        statusAssessedOnFrom: filters.recommendation.drpFilter?.statusAssessedOnFrom,
        statusAssessedOnTo: filters.recommendation.drpFilter?.statusAssessedOnTo,
        statusAssessedOnFloatingShortcutName: filters.recommendation.drpFilter?.statusAssessedOnFloatingShortcutName,
        notesSearchString: filters.recommendation.drpFilter?.notesSearchString,
      },

      page: filters.recommendationPaging.page ?? 0,
      size: filters.recommendationPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    filters.recommendationSorting,
    {
      visit: ['PATIENT_LAST_NAME', 'PATIENT_FIRST_NAME', 'ORGANIZATION_NAME', 'ENCOUNTER_START_DATE'],
    }
  );
  const {data, isLoading, page, countOnPage, totalCount, totalPages} = useTableData<any, RowType[]>(
    recommendationsUrl,
    recommendationsParams
  );
  const {handleXlsxExport, warningDialog} = usePhRecommendationsExport(filters.recommendation, config, totalCount);

  const formatters = useFormatters();
  const {t, formatDate, tEnum} = formatters;
  const columns = useColumns(formatters, filters.recommendationSorting, config);

  const filtersRef = useRef<HTMLDivElement | null>(null);
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  // Fullscreen results table, the same way the report tables do it.
  const {
    maximized: tableMaximized,
    toggleMaximized: tableToggleMaximized,
    MaximizeWrapper: TableMaximizeWrapper,
  } = useMaximizeWrapper('patient-list-wrapper', {innerStyle: {rowGap: 16}});
  // Fullscreen filter panel (PDM-2842): this wrapper IS the panel's card (16px padding, no bottom padding so the
  // content scrolls to the card edge); the panel itself owns the button and keeps the flag consistent with "open".
  const {
    maximized: filterPanelMaximized,
    setMaximized: setFilterPanelMaximized,
    MaximizeWrapper: FilterPanelMaximizeWrapper,
  } = useMaximizeWrapper('filter-panel-wrapper', {innerStyle: {rowGap: 16, paddingBottom: 0}});

  const {instanceLocation} = useInstanceLocation(config);
  const {renderOption: atcRenderOption, renderTag: atcRenderTag, getNodeIcon: atcGetNodeIcon} = useInactiveAtcRenderProps(t);
  const {data: organizations} = useOrganizations(config);
  // Chips pane works with a FLAT filter map — nested drpFilter fields are exposed as `drpFilter_<key>`
  // and folded back into the nested shape in changeTableFilters.
  const tableFiltersForSelectedPane = useMemo(() => {
    const {drpFilter, ...rest} = filters.recommendation;
    const result: Record<string, any> = {...rest};
    Object.entries(drpFilter ?? {}).forEach(([key, value]) => {
      result[`drpFilter_${key}`] = value;
    });
    return result;
  }, [filters.recommendation]);

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }

  const sortByMessageKey = (options) => sortBy(options, [(v) => t(v.messageKey)]);
  const getOrganizationLabel = (id: unknown) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id);
  const getMedicationAtcLabel = (id: unknown) => medicationAtcOptions?.find((w) => String(w.id) === String(id))?.label ?? String(id);
  const changeTableFiltersFromPane = (newTableFilters: Record<string, any>) => {
    const newRecommendation: Record<string, any> = {};
    const newDrpFilter: Record<string, any> = {};
    Object.entries(newTableFilters).forEach(([key, value]) => {
      if (key.startsWith('drpFilter_')) {
        newDrpFilter[key.slice('drpFilter_'.length)] = value;
      } else {
        newRecommendation[key] = value;
      }
    });
    changeFilters({
      recommendation: {
        ...newRecommendation,
        drpFilter: Object.keys(omitNullsy(newDrpFilter)).length > 0 ? newDrpFilter : undefined,
      } as PhRecPageFilters,
      recommendationPaging: {
        ...filters.recommendationPaging,
        page: 0,
      },
    });
  };
  // Every chip belongs to one filter-panel tab: the tab badges count the chips of their tab, the "Active filters"
  // heading counts them all. The lists mirror the field order of the tab panels below.
  const withTab = (tab: PhRecFilterTab, items: SelectedFilter[]): SelectedFilter[] => items.map((v) => ({...v, tab}));
  const selectedFilters: SelectedFilter[] = [
    ...withTab('general', [
      {
        name: 'patientFullName',
        label: t('filter.patient.fullName'),
        value: filters.recommendation.patientFullName,
      },
      {
        name: 'patientIdentificationNumber',
        label: t('filter.patient.identificationNumber'),
        value: filters.recommendation.patientIdentificationNumber,
      },
      {
        name: 'patientSrcId',
        label: t('filter.patient.sourceId'),
        value: filters.recommendation.patientSrcId,
      },
      {
        name: ['encounterStartedFrom', 'encounterStartedTo', 'encounterFloatingShortcutName'],
        label: t('filter.recommendation.encounterDate'),
        value: `${formatDate(filters.recommendation.encounterStartedFrom ?? '')} - ${formatDate(
          filters.recommendation.encounterStartedTo ?? ''
        )}`,
      },
      {
        name: 'pharmaRecommendationOrganizationIds',
        label: t('filter.recommendation.organizationIds'),
        value: (filters.recommendation.pharmaRecommendationOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['pharmaRecommendationDateFrom', 'pharmaRecommendationDateTo', 'pharmaRecommendationFloatingShortcutName'],
        label: t('filter.recommendation.date'),
        value: `${formatDate(filters.recommendation.pharmaRecommendationDateFrom ?? '')} - ${formatDate(
          filters.recommendation.pharmaRecommendationDateTo ?? ''
        )}`,
      },
      {
        name: 'wasPublished',
        label: t('filter.recommendation.wasPublished'),
        value: t(filters.recommendation.wasPublished ? 'global.boolFilter.true' : 'global.boolFilter.false'),
      },
      {
        name: 'riskiness',
        label: t('filter.recommendation.riskiness'),
        value: (filters.recommendation.riskiness ?? []).map((v) => tEnum('Riskiness', v)).join(', '),
      },
      {
        name: 'riskFactorTypes',
        label: t('filter.patient.riskFactorTypes'),
        value: (filters.recommendation.riskFactorTypes ?? []).map((v) => tEnum('RiskFactorTypeEnum', v)).join(', '),
      },
      {
        name: 'summaryAndConclusionSearchString',
        label: t('filter.recommendation.summaryAndConclusionSearchString'),
        value: filters.recommendation.summaryAndConclusionSearchString,
      },
      {
        name: 'hasDrp',
        label: t('filter.recommendation.hasDrp'),
        value: t(filters.recommendation.hasDrp ? 'global.boolFilter.true' : 'global.boolFilter.false'),
      },
      {
        name: ['followUpDateFrom', 'followUpDateTo', 'followUpFloatingShortcutName'],
        label: t('filter.recommendation.followUpDate'),
        value: `${formatDate(filters.recommendation.followUpDateFrom ?? '')} - ${formatDate(filters.recommendation.followUpDateTo ?? '')}`,
      },
      {
        name: 'procedures',
        label: t('filter.recommendation.procedures'),
        value: (filters.recommendation.procedures ?? []).map((v) => tEnum('Procedure', v)).join(', '),
      },
    ]),
    ...withTab('drp', [
      {
        name: 'drpFilter_medicationName_local',
        label: t('hospitalization.detail.phRec.drp.medicationName'),
        value: isFreeSolo(filters.recommendation.drpFilter?.medicationName_local)
          ? (filters.recommendation.drpFilter?.medicationName_local as {freeSolo: string}).freeSolo
          : (filters.recommendation.drpFilter?.medicationName_local as string | undefined),
      },
      {
        name: 'drpFilter_medicationAtcId',
        label: t('hospitalization.detail.phRec.drp.medicationAtcId'),
        value: (filters.recommendation.drpFilter?.medicationAtcId ?? []).map(getMedicationAtcLabel).join(', '),
      },
      {
        name: 'drpFilter_types',
        label: t('filter.recommendation.drpFilterTypes'),
        value: (filters.recommendation.drpFilter?.types ?? []).map((v) => tEnum('DrpTypeAllRecommendationsListFilter', v)).join(', '),
      },
      {
        name: 'drpFilter_causes',
        label: t('filter.recommendation.drpFilterCauses'),
        value: (filters.recommendation.drpFilter?.causes ?? []).map((v) => tEnum('DrpCause', v)).join(', '),
      },
      {
        name: 'drpFilter_plannedInterventions',
        label: t('filter.recommendation.drpFilterPlannedInterventions'),
        value: (filters.recommendation.drpFilter?.plannedInterventions ?? []).map((v) => tEnum('DrpPlannedIntervention', v)).join(', '),
      },
      {
        name: 'drpFilter_manifestation',
        label: t('filter.recommendation.drpFilterManifestation'),
        value: tEnum('DrpManifestation', filters.recommendation.drpFilter?.manifestation),
      },
      {
        name: 'drpFilter_interventionAcceptances',
        label: t('filter.recommendation.drpFilterInterventionAcceptances'),
        value: (filters.recommendation.drpFilter?.interventionAcceptances ?? [])
          .map((v) => tEnum('DrpInterventionAcceptance', v))
          .join(', '),
      },
      {
        name: 'drpFilter_interventionNotAcceptedReasons',
        label: t('filter.recommendation.drpFilterInterventionNotAcceptedReasons'),
        value: (filters.recommendation.drpFilter?.interventionNotAcceptedReasons ?? [])
          .map((v) => tEnum('DrpInterventionNotAcceptedReasonAllRecommendationsListFilter', v))
          .join(', '),
      },
      {
        name: 'drpFilter_statuses',
        label: t('filter.recommendation.drpFilterStatuses'),
        value: (filters.recommendation.drpFilter?.statuses ?? []).map((v) => tEnum('DrpStatus', v)).join(', '),
      },
      {
        name: ['drpFilter_statusAssessedOnFrom', 'drpFilter_statusAssessedOnTo', 'drpFilter_statusAssessedOnFloatingShortcutName'],
        label: t('filter.recommendation.drpFilterStatusAssessedOn'),
        value: `${formatDate(filters.recommendation.drpFilter?.statusAssessedOnFrom ?? '')} - ${formatDate(
          filters.recommendation.drpFilter?.statusAssessedOnTo ?? ''
        )}`,
      },
      {
        name: 'drpFilter_notesSearchString',
        label: t('filter.recommendation.drpFilterNotesSearchString'),
        value: filters.recommendation.drpFilter?.notesSearchString,
      },
    ]),
  ];
  const selectedFiltersCount = countSelectedFilters(selectedFilters, tableFiltersForSelectedPane);
  const filterTabs: LightFilterTabDef[] = [
    {id: 'general', label: t('filter.tabs.general'), iconName: 'hospitalUserIcon', count: selectedFiltersCount.byTab.general},
    {id: 'drp', label: t('filter.tabs.drp'), iconName: 'triangleExclamationIcon', count: selectedFiltersCount.byTab.drp},
  ];
  const selectedFiltersPane =
    selectedFiltersCount.total > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={selectedFilters}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={changeTableFiltersFromPane}
        formatters={formatters}
      />
    ) : null;
  // The inputs of both tabs; the panel keeps them mounted and shows the active one.
  const tabPanels: Record<PhRecFilterTab, React.ReactNode> = {
    general: (
      <>
        <LightTextFilter
          config={{
            name: 'patientFullName',
            ui: {label: 'filter.patient.fullName'},
          }}
          value={filters.recommendation.patientFullName}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                patientFullName: newValue ?? undefined,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientIdentificationNumber',
            ui: {label: 'filter.patient.identificationNumber'},
          }}
          value={filters.recommendation.patientIdentificationNumber}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                patientIdentificationNumber: newValue ?? undefined,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientSrcId',
            ui: {label: 'filter.patient.sourceId'},
          }}
          value={filters.recommendation.patientSrcId}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                patientSrcId: newValue ?? undefined,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'admissionDate',
            ui: {label: 'filter.recommendation.encounterDate'},
          }}
          value={
            filters.recommendation.encounterStartedFrom && filters.recommendation.encounterStartedTo
              ? {
                  startDate: filters.recommendation.encounterStartedFrom,
                  endDate: filters.recommendation.encounterStartedTo,
                  shortcut: filters.recommendation.encounterFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                encounterStartedFrom: newValue?.startDate,
                encounterStartedTo: newValue?.endDate,
                encounterFloatingShortcutName: newValue?.shortcut,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightOrganizationFilter2
          appConfig={config}
          config={{
            name: 'pharmaRecommendationOrganizationIds',
            ui: {label: 'filter.recommendation.organizationIds'},
          }}
          multi
          value={filters.recommendation.pharmaRecommendationOrganizationIds?.map((orgId) => orgId.toString())}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                pharmaRecommendationOrganizationIds: newValue?.map((str) => Number(str)) ?? undefined,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'pharmaRecommendationDate',
            ui: {label: 'filter.recommendation.date'},
          }}
          value={
            filters.recommendation.pharmaRecommendationDateFrom && filters.recommendation.pharmaRecommendationDateTo
              ? {
                  startDate: filters.recommendation.pharmaRecommendationDateFrom,
                  endDate: filters.recommendation.pharmaRecommendationDateTo,
                  shortcut: filters.recommendation.pharmaRecommendationFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                pharmaRecommendationDateFrom: newValue?.startDate,
                pharmaRecommendationDateTo: newValue?.endDate,
                pharmaRecommendationFloatingShortcutName: newValue?.shortcut,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightSelectFilter
          config={{
            name: 'wasPublished',
            ui: {label: 'filter.recommendation.wasPublished'},
          }}
          options={BOOL_FILTER_OPTIONS}
          maxWidth={'176px'}
          isDefaultValue={filters.recommendation.wasPublished === undefined}
          value={getSingleBoolFilterOptionId(filters.recommendation.wasPublished)}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                wasPublished: getSingleBoolFilterValue(newValue),
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'riskiness',
            ui: {label: 'filter.recommendation.riskiness'},
          }}
          options={sortByMessageKey(enumMap.Riskiness ?? [])}
          multi
          maxWidth={'176px'}
          value={filters.recommendation.riskiness}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                riskiness: newValue,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'riskFactorTypes',
            ui: {label: 'filter.patient.riskFactorTypes'},
          }}
          options={sortByMessageKey(enumMap.RiskFactorTypeEnum ?? [])}
          multi
          value={filters.recommendation.riskFactorTypes}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                riskFactorTypes: newValue,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'summaryAndConclusionSearchString',
            ui: {label: 'filter.recommendation.summaryAndConclusionSearchString'},
          }}
          value={filters.recommendation.summaryAndConclusionSearchString}
          wrapperWidth={'362px'}
          inputWidth={'100%'}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                summaryAndConclusionSearchString: newValue ?? undefined,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'hasDrp',
            ui: {label: 'filter.recommendation.hasDrp'},
          }}
          options={BOOL_FILTER_OPTIONS}
          isDefaultValue={filters.recommendation.hasDrp === undefined}
          value={getSingleBoolFilterOptionId(filters.recommendation.hasDrp)}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                hasDrp: getSingleBoolFilterValue(newValue),
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'followUpDate',
            ui: {label: 'filter.recommendation.followUpDate'},
          }}
          value={
            filters.recommendation.followUpDateFrom && filters.recommendation.followUpDateTo
              ? {
                  startDate: filters.recommendation.followUpDateFrom,
                  endDate: filters.recommendation.followUpDateTo,
                  shortcut: filters.recommendation.followUpFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                followUpDateFrom: newValue?.startDate,
                followUpDateTo: newValue?.endDate,
                followUpFloatingShortcutName: newValue?.shortcut,
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          enableFuture
          showShortcuts
        />
        {instanceLocation?.toUpperCase() === 'CZ' && (
          <LightSelectFilter
            config={{
              name: 'procedures',
              ui: {label: 'filter.recommendation.procedures'},
            }}
            options={sortByMessageKey(enumMap.Procedure ?? [])}
            multi
            value={filters.recommendation.procedures}
            onChange={(newValue) => {
              changeFilters({
                recommendation: {
                  ...filters.recommendation,
                  procedures: newValue,
                },
                recommendationPaging: {
                  ...filters.recommendationPaging,
                  page: 0,
                },
              });
            }}
          />
        )}
      </>
    ),
    drp: (
      <>
        <LightSelectFilterVirtualized
          config={{name: 'medicationName', ui: {label: 'hospitalization.detail.phRec.drp.medicationName'}}}
          options={medicationNameOptions}
          freeSolo
          value={filters.recommendation.drpFilter?.medicationName_local}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  medicationName_local: newValue,
                  medicationAtcId: isKnownMedicationName(newValue) ? undefined : filters.recommendation.drpFilter?.medicationAtcId,
                },
              },
            });
          }}
        />
        <LightHierarchyFilter
          config={{name: 'medicationAtcId', ui: {label: 'hospitalization.detail.phRec.drp.medicationAtcId'}}}
          options={medicationAtcOptions}
          multi
          value={
            filters.recommendation.drpFilter?.medicationAtcId != null
              ? filters.recommendation.drpFilter?.medicationAtcId.map((value) => String(value))
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  medicationAtcId: newValue != null ? newValue.map((value: string | number) => +value) : undefined,
                },
              },
            });
          }}
          ignoreOption={(option: NormalizedOption<MedicationAtc>) => option.original.isSoloRootNode}
          getNodeIcon={(params) => atcGetNodeIcon(params.node)}
          renderOption={atcRenderOption}
          renderTag={atcRenderTag}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.types',
            ui: {label: 'filter.recommendation.drpFilterTypes'},
          }}
          options={sortByMessageKey(enumMap.DrpTypeAllRecommendationsListFilter ?? [])}
          multi
          value={filters.recommendation.drpFilter?.types}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  types: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightHierarchyFilter
          config={{
            name: 'drpFilter.causes',
            ui: {label: 'filter.recommendation.drpFilterCauses'},
          }}
          options={sortByMessageKey(enumMap.DrpCause ?? [])}
          multi
          value={filters.recommendation.drpFilter?.causes}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  causes: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.plannedInterventions',
            ui: {label: 'filter.recommendation.drpFilterPlannedInterventions'},
          }}
          options={sortByMessageKey(enumMap.DrpPlannedIntervention ?? [])}
          multi
          value={filters.recommendation.drpFilter?.plannedInterventions}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  plannedInterventions: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.manifestation',
            ui: {label: 'filter.recommendation.drpFilterManifestation'},
          }}
          options={sortByMessageKey(enumMap.DrpManifestation ?? [])}
          value={filters.recommendation.drpFilter?.manifestation}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  manifestation: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.interventionAcceptances',
            ui: {label: 'filter.recommendation.drpFilterInterventionAcceptances'},
          }}
          options={sortByMessageKey(enumMap.DrpInterventionAcceptance ?? [])}
          multi
          value={filters.recommendation.drpFilter?.interventionAcceptances}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  interventionAcceptances: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.interventionNotAcceptedReasons',
            ui: {label: 'filter.recommendation.drpFilterInterventionNotAcceptedReasons'},
          }}
          options={sortByMessageKey(enumMap.DrpInterventionNotAcceptedReasonAllRecommendationsListFilter ?? [])}
          multi
          value={filters.recommendation.drpFilter?.interventionNotAcceptedReasons}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  interventionNotAcceptedReasons: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'drpFilter.statuses',
            ui: {label: 'filter.recommendation.drpFilterStatuses'},
          }}
          options={sortByMessageKey(enumMap.DrpStatus ?? [])}
          multi
          value={filters.recommendation.drpFilter?.statuses}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  statuses: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'drpFilter.statusAssessedOn',
            ui: {label: 'filter.recommendation.drpFilterStatusAssessedOn'},
          }}
          value={
            filters.recommendation?.drpFilter?.statusAssessedOnFrom && filters.recommendation?.drpFilter?.statusAssessedOnTo
              ? {
                  startDate: filters.recommendation.drpFilter.statusAssessedOnFrom,
                  endDate: filters.recommendation.drpFilter.statusAssessedOnTo,
                  shortcut: filters.recommendation.statusAssessedOnFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  statusAssessedOnFrom: newValue?.startDate ?? undefined,
                  statusAssessedOnTo: newValue?.endDate ?? undefined,
                  statusAssessedOnFloatingShortcutName: newValue?.shortcut ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightTextFilter
          config={{
            name: 'drpFilter.notesSearchString',
            ui: {label: 'filter.recommendation.drpFilterNotesSearchString'},
          }}
          wrapperWidth={'362px'}
          inputWidth={'100%'}
          value={filters.recommendation.drpFilter?.notesSearchString}
          onChange={(newValue) => {
            changeFilters({
              recommendation: {
                ...filters.recommendation,
                drpFilter: {
                  ...(filters.recommendation.drpFilter ?? {}),
                  notesSearchString: newValue ?? undefined,
                },
              },
              recommendationPaging: {
                ...filters.recommendationPaging,
                page: 0,
              },
            });
          }}
        />
      </>
    ),
  };
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={t('navigation.route.pharmacotherapyRecommendationList')} />
      <LightSplitLayout
        initSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].sizes}
        minSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].minSize}
        storageId={PATIENTS_LAYOUT_SPLIT_CONFIG[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.recommendationOpen, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.recommendationOpen}}
        hidden={[false, false]}
      >
        <FilterPanelMaximizeWrapper>
          <LightFilterPanel
            open={filters.recommendationOpen}
            toggleOpen={() =>
              changeFilters({
                recommendationOpen: !filters.recommendationOpen,
              })
            }
            hasActiveFilter={hasFilters}
            scrollAreaRef={filtersRef}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.recommendation,
              setFilter: (newFilter) => {
                changeFilters({
                  recommendation: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizePhRecFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
            selectedFiltersCount={selectedFiltersCount.total}
            maximize={{maximized: filterPanelMaximized, setMaximized: setFilterPanelMaximized}}
            tabs={{
              tabs: filterTabs,
              activeTab: filters.recommendationTab,
              onChange: (tabId, open) =>
                changeFilters({recommendationTab: tabId as PhRecFilterTab, ...(open ? {recommendationOpen: true} : {})}),
              panels: tabPanels,
            }}
          />
        </FilterPanelMaximizeWrapper>
        <TableMaximizeWrapper>
          <IconHeaderWithSearch
            iconName={'listPlusIcon'}
            label={'navigation.route.pharmacotherapyRecommendationList'}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={() => {
                changeFilters({
                  recommendation: DEFAULT_RECOMMENDATION_FILTERS,
                  recommendationPaging: {
                    ...filters.recommendationPaging,
                    page: 0,
                  },
                });
              }}
              hasSorting={!isEqual(filters.recommendationSorting, DEFAULT_RECOMMENDATION_SORTING)}
              handleResetSorting={() => {
                changeFilters({
                  recommendationSorting: DEFAULT_RECOMMENDATION_SORTING,
                });
              }}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
            />
            <MaximizeButton maximized={tableMaximized} toggleMaximized={tableToggleMaximized} />
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            variant='big'
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
              padding: 'dense',
            }}
            cellLineHeight='2.2'
            useMaxHeight
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                recommendationPaging: {
                  ...filters.recommendationPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                recommendationPaging: {
                  ...filters.recommendationPaging,
                  pageSize,
                  page: clampPageNumber(filters.recommendationPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.recommendationPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                recommendationSorting: {
                  field: columnId,
                  direction,
                },
                recommendationPaging: {
                  ...filters.recommendationPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.recommendationSorting;
            })()}
            scrollStorageId='/PharmacotherapyRecommendationListLayout.scrollXY'
          />
          {warningDialog}
        </TableMaximizeWrapper>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (_options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
